import { BasePrice, PaymentPlan, PriceV2, PriceV2ApiResponse } from 'types'

/**
 * Return formatted price to pounds
 * @param {number} price - The price in pence from the backend
 */
export const priceToPounds = (price: number) => (price / 100).toFixed(2)

export type TransformedPrice =
  | {
      paymentPlan: PaymentPlan.ANNUAL
      priceAsFixedPoint: number
    }
  | {
      paymentPlan: PaymentPlan.MONTHLY
      priceAsFixedPoint: number
      firstMonthPriceAsFixedPoint: number
      totalPayableAsFixedPoint: number
      instalmentCount: number
    }
export const initGetPriceFunction =
  (paymentPlan: PaymentPlan) =>
  (premiumPrice: BasePrice): TransformedPrice => {
    const { annual, monthly } = premiumPrice

    if (paymentPlan === PaymentPlan.MONTHLY) {
      return {
        paymentPlan,
        priceAsFixedPoint: monthly.monthlyTotal.amountAsFixedPoint,
        firstMonthPriceAsFixedPoint: monthly.upFrontTotal.amountAsFixedPoint,
        totalPayableAsFixedPoint: monthly.totalAmountRepayable.amountAsFixedPoint,
        instalmentCount: monthly.instalments,
      }
    }

    return {
      paymentPlan,
      priceAsFixedPoint: annual.upFrontTotal.amountAsFixedPoint,
    }
  }

export const initGetPriceV2Function =
  (paymentPlan: PaymentPlan) =>
  (premiumPrice: PriceV2, priceData: PriceV2ApiResponse): TransformedPrice => {
    const { annual, monthly } = premiumPrice

    if (paymentPlan === PaymentPlan.MONTHLY) {
      return {
        paymentPlan,
        priceAsFixedPoint: monthly.total.perMonth,
        firstMonthPriceAsFixedPoint: monthly.total.upfront,
        totalPayableAsFixedPoint: monthly.total.perYear,
        instalmentCount: priceData.paymentSchedule.numberOfInstalments,
      }
    }

    return {
      paymentPlan,
      priceAsFixedPoint: annual.total.perYear,
    }
  }

/**
 * Return formatted price to pounds and uk locale
 * @param {number} price - The price in pence from the backend
 */
export const priceToUkLocale = (
  price: number,
  showDecimal: boolean = true,
  isAlreadyPounds: boolean = false,
) => {
  // returns price to 2 decimal points as string
  const formattedPrice = isAlreadyPounds ? price.toFixed(2) : priceToPounds(price)

  const parts = formattedPrice.split('.')
  parts[0] = Number(parts[0]).toLocaleString('en-UK')
  return showDecimal ? parts.join('.') : parts[0]
}

/**
 * Return formatted price to pounds and UK locale with pound sign
 * @param {number} price - The price in pence from the backend
 */
export const formatPrice = (
  price: number,
  showDecimal: boolean = true,
  isAlreadyPounds: boolean = false,
) => {
  const isNegative = price < 0
  const formattedPrice = priceToUkLocale(price, showDecimal, isAlreadyPounds)
  return isNegative ? `-£${formattedPrice.slice(1)}` : `£${formattedPrice}`
}
